import { isNativePlatform } from './auth/is-native';

type AppEnv = {
  apiBaseUrl?: string;
  frontendBaseUrl?: string;
};

function readViteEnv(key: keyof AppEnv): string | undefined {
  const env = import.meta.env;
  if (key === 'apiBaseUrl') {
    return env.VITE_API_BASE_URL as string | undefined;
  }
  if (key === 'frontendBaseUrl') {
    return env.VITE_FRONTEND_BASE_URL as string | undefined;
  }
  return undefined;
}

function isBrowserOnPublicHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname !== 'localhost' && hostname !== '127.0.0.1';
}

function isLocalDevUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'api.localhost';
  } catch {
    return false;
  }
}

function shouldIgnoreBakedUrl(url: string | undefined): boolean {
  if (!url) {
    return true;
  }

  return isBrowserOnPublicHost() && isLocalDevUrl(url);
}

function deriveApiFromWindow(): string {
  if (typeof window === 'undefined') {
    return 'http://localhost:3000';
  }

  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }

  return `${protocol}//api.${hostname}`;
}

function readBakedUrl(key: keyof AppEnv): string | undefined {
  const fromEnv = readViteEnv(key);
  if (!fromEnv || shouldIgnoreBakedUrl(fromEnv)) {
    return undefined;
  }
  return fromEnv;
}

/** True when a build-time URL belongs to the page the user actually opened. */
export function bakedUrlMatchesPageHost(url: string, pageHostname: string): boolean {
  if (pageHostname === 'localhost' || pageHostname === '127.0.0.1') {
    return true;
  }
  try {
    const urlHost = new URL(url).hostname;
    return urlHost === pageHostname || urlHost === `api.${pageHostname}`;
  } catch {
    return false;
  }
}

/** API origin — set via VITE_API_BASE_URL at build time when needed. */
export function resolveApiBaseUrlForContext(
  bakedApiUrl: string | undefined,
  pageLocation: Pick<Location, 'hostname' | 'protocol'> | undefined,
): string {
  const pageHostname = pageLocation?.hostname;
  if (
    bakedApiUrl &&
    pageHostname &&
    bakedUrlMatchesPageHost(bakedApiUrl, pageHostname) &&
    !shouldIgnoreBakedUrl(bakedApiUrl)
  ) {
    return bakedApiUrl;
  }

  if (bakedApiUrl && !pageHostname && !shouldIgnoreBakedUrl(bakedApiUrl)) {
    return bakedApiUrl;
  }

  if (!pageLocation) {
    return deriveApiFromWindow();
  }

  const { protocol, hostname } = pageLocation;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }

  return `${protocol}//api.${hostname}`;
}

export function getApiBaseUrl(): string {
  if (isNativePlatform()) {
    const baked = readBakedUrl('apiBaseUrl');
    if (baked) {
      return baked;
    }
  }

  const pageLocation =
    typeof window !== 'undefined'
      ? { hostname: window.location.hostname, protocol: window.location.protocol }
      : undefined;
  return resolveApiBaseUrlForContext(readViteEnv('apiBaseUrl'), pageLocation);
}

/** Public frontend origin for OAuth callbacks. */
export function resolveFrontendBaseUrlForContext(
  bakedUrl: string | undefined,
  windowLocation: Pick<Location, 'hostname' | 'origin'> | undefined,
): string {
  if (bakedUrl) {
    return bakedUrl;
  }

  if (windowLocation) {
    return windowLocation.origin;
  }

  return 'http://localhost:5173';
}

function readFrontendBakedUrl(): string | undefined {
  const baked = readBakedUrl('frontendBaseUrl');
  if (!baked) {
    return undefined;
  }
  if (isNativePlatform() || typeof window === 'undefined') {
    return baked;
  }
  const pageHost = window.location.hostname;
  if (pageHost === 'localhost' || pageHost === '127.0.0.1') {
    return baked;
  }
  try {
    return new URL(baked).hostname === pageHost ? baked : undefined;
  } catch {
    return undefined;
  }
}

export function getFrontendBaseUrl(): string {
  // Capacitor WebView is https://localhost — never use that when a baked URL exists.
  const baked = readFrontendBakedUrl();
  const windowLocation =
    typeof window !== 'undefined'
      ? { hostname: window.location.hostname, origin: window.location.origin }
      : undefined;
  return resolveFrontendBaseUrlForContext(baked, windowLocation);
}
